import {CommentsCategory, type CommentDto, useAddComment, useEditComment} from '@domain/comments'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantContext} from '@hconnect/upm'
import {ArrowForward, Close, DeleteOutline} from '@mui/icons-material'
import {Box, Button} from '@mui/material'
import {FC} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {PLANNING_COMMENTS_WIDGET_NAMESPACE} from '../consts'
import {useCommentsPermissions, useConfirmDialog, useDeleteCommentMutation} from '../hooks'
import {useSelectedAssetId} from '../providers'

import {CommentInput} from './CommentInput'

interface AssetCommentFormProps {
  timezoneId: string
  assetId: number
  comment?: CommentDto
}

export const AssetCommentForm: FC<AssetCommentFormProps> = ({comment, timezoneId, assetId}) => {
  const {t} = useTranslation(PLANNING_COMMENTS_WIDGET_NAMESPACE)
  const {plantId: plantCode} = useUpmPlantContext()
  const {setAssetId} = useSelectedAssetId()
  const {mutateAsync: addComment} = useAddComment(
    {plantId: plantCode, commentsCategory: CommentsCategory.Assets},
    'comments'
  )
  const {mutateAsync: editComment} = useEditComment(
    {plantId: plantCode, commentsCategory: CommentsCategory.Assets},
    'comments'
  )
  const {mutate: deleteComment} = useDeleteCommentMutation()
  const {openDialog, ConfirmDialog} = useConfirmDialog()
  const commentInitialState: Pick<CommentDto, 'value'> = {value: comment?.value ?? ''}
  const {register, formState, reset, handleSubmit, watch} = useForm<typeof commentInitialState>({
    defaultValues: commentInitialState
  })
  const {canDeleteComments} = useCommentsPermissions(CommentsCategory.Assets)
  const isSubmitting = formState.isSubmitting
  const currentCommentValue = watch('value')

  const handleCommentSubmit = async (data: typeof commentInitialState) => {
    if (comment) {
      await editComment({
        value: data.value,
        id: comment.commentId,
        commentCategoryInstanceId: assetId.toString()
      })
    } else {
      await addComment({
        value: data.value,
        commentCategoryInstanceId: assetId.toString()
      })
    }
  }

  const saveComment = async () => {
    if (currentCommentValue) {
      await handleSubmit(handleCommentSubmit)()
    }
    setAssetId(undefined)
  }

  const onDeleteComment = () => {
    if (!comment) throw new Error('Comment is missing')
    openDialog({
      title: t('planning.deleteComment'),
      description: t('planning.deleteCommentConfirmation'),
      mainAction: {
        color: 'error',
        text: t('common.delete'),
        icon: <DeleteOutline />,
        onAction: () => {
          deleteComment({
            plantId: plantCode,
            commentCategoryInstanceId: assetId.toString(),
            commentId: comment.commentId,
            commentsCategory: comment.commentsCategory
          })
          setAssetId(undefined)
        }
      }
    })
  }

  const isSaveButtonDisabled = isSubmitting || !currentCommentValue

  return (
    <Box mb={2}>
      {ConfirmDialog}
      <CommentInput
        testId="asset_comment_input"
        inputProps={{
          ...register('value')
        }}
        comment={comment}
        currentCommentValue={currentCommentValue}
        timezoneId={timezoneId}
        onDelete={() => reset({value: ''})}
      />
      <Box mt={2} display="flex" justifyContent="space-between">
        <Button
          {...dataTestId('asset_comment_delete')}
          color="secondary"
          startIcon={<DeleteOutline color="error" />}
          onClick={onDeleteComment}
          disabled={!canDeleteComments || !comment}
        >
          {t('common.delete')}
        </Button>
        <Box display="flex" alignItems="center">
          <Button
            {...dataTestId('asset_comment_cancel')}
            color="secondary"
            startIcon={<Close />}
            onClick={() => setAssetId(undefined)}
          >
            {t('common.cancel')}
          </Button>
          <Box ml={2}>
            <Button
              {...dataTestId('asset_comment_save')}
              color="primary"
              variant="contained"
              startIcon={<ArrowForward />}
              onClick={() => void saveComment()}
              disabled={isSaveButtonDisabled}
            >
              {t('common.save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
